import type { RegistryStatus } from '@eduzz-registry-360/validators';
import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';

export type Registry = {
  fullName: string;
  document: string;
  signerDocument: string;
  personType: 'F' | 'J' | 'E';
  occupation: string;
  income: string;
  legalEntityIncome: string;
  faceId: string;
  representativeDocumentFile: string;
  representativeDocumentStatus: RegistryStatus;
  socialContractFiles: { hash: string; fileName: string }[];
  socialContractStatus: RegistryStatus;
  legalEntityRelation?: 'none' | 'partner' | 'representative';
  cep: string;
  stateName: string;
  stateCode: string;
  cityName: string;
  cityCode: string;
  neighborhood: string;
  street: string;
  number: string;
  noNumber?: boolean;
  complement: string;
  reference: string;
  pep: '' | 'true' | 'false';
  phone: string;
  bank: string;
  agency: string;
  agencyDigit: string;
  account: string;
  accountDigit: string;
  accountType: string;
  bankAccountOwner: string;
  bankAccountDocument: string;
  bankAccountType: '' | 'personal' | 'company';
  fidcTerms?: boolean;
  useTerms?: boolean;
  fiscalResponsibility?: boolean;
  infoTruthfulness?: boolean;
  documentType?: 'cnh' | 'rg' | 'cin' | 'passport';
  documentFileMode: 'single' | 'multiple';
  documentFile?: string;
  documentFileBack?: string;
  documentStatus: RegistryStatus;
  companyType?: string;
  isValidCompanyPartner?: boolean;
  isPending?: boolean;
};

export type RegistryStore = {
  registry: Registry;
  setFields: (registry: Partial<Registry>) => void;
};

export const initialRegistry: Registry = {
  fullName: '',
  document: '',
  signerDocument: '',
  personType: 'F',
  occupation: '',
  income: '',
  legalEntityIncome: '',
  faceId: '',
  representativeDocumentFile: '',
  representativeDocumentStatus: 'incomplete',
  socialContractFiles: [],
  socialContractStatus: 'incomplete',
  legalEntityRelation: undefined,
  cep: '',
  stateName: '',
  stateCode: '',
  cityName: '',
  cityCode: '',
  neighborhood: '',
  street: '',
  number: '',
  noNumber: undefined,
  complement: '',
  reference: '',
  pep: '',
  phone: '',
  bank: '',
  agency: '',
  agencyDigit: '',
  account: '',
  accountDigit: '',
  accountType: '',
  bankAccountOwner: '',
  bankAccountDocument: '',
  bankAccountType: '',
  documentType: 'rg',
  documentFileMode: 'single',
  documentStatus: 'incomplete',
  companyType: '',
  isValidCompanyPartner: false
};

const normalizeFields = (fields: Partial<Registry>): Partial<Registry> => {
  const incoming = fields as Partial<Registry> & { pep?: unknown };

  if (typeof incoming.pep !== 'boolean') {
    return fields;
  }

  return { ...fields, pep: incoming.pep ? 'true' : 'false' };
};

export const registryStore = create<RegistryStore>()(set => ({
  registry: initialRegistry,
  setFields: (fields: Partial<Registry>) => {
    set(({ registry }) => ({ registry: { ...registry, ...normalizeFields(fields) } }));
  }
}));

const useRegistryStore = <T>(selector: (state: RegistryStore) => T): T => registryStore(useShallow(selector));

export default useRegistryStore;
